import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch, setToken, clearToken } from '../lib/api';
import { API_BASE_URL } from '../config';

const SHOPLINE_URL = 'https://shopline-frontend.pages.dev';

export type AuthUser = { id: number; email: string; is_admin?: number; };
type AuthContextValue = {
  user: AuthUser | null; loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<{ user: AuthUser }>('/api/auth/me');
      setUser(data.user);
    } catch {
      setUser(null); clearToken();
      window.location.href = SHOPLINE_URL;
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const ssoToken = params.get('token');
      if (ssoToken) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/auth/sso`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: ssoToken }),
          });
          const data = await res.json();
          if (data.success && data.token) {
            setToken(data.token);
            setUser(data.user);
            if (data.shoplineUserId) localStorage.setItem('shopline_user_id', String(data.shoplineUserId));
            const url = new URL(window.location.href);
            url.searchParams.delete('token'); url.searchParams.delete('email'); url.searchParams.delete('name');
            window.history.replaceState({}, '', url.toString());
            setLoading(false); return;
          }
        } catch {}
        window.location.href = SHOPLINE_URL; return;
      }
      const saved = localStorage.getItem('auth_token');
      if (!saved) { setLoading(false); window.location.href = SHOPLINE_URL; return; }
      await refresh();
    };
    init();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ user: AuthUser; token: string }>('/api/auth/login', { method: 'POST', json: { email, password } });
    if (data.token) setToken(data.token);
    await refresh();
  }, [refresh]);

  const register = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ user: AuthUser; token: string }>('/api/auth/register', { method: 'POST', json: { email, password } });
    if (data.token) setToken(data.token);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    clearToken(); setUser(null);
    window.location.href = SHOPLINE_URL;
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ user, loading, refresh, login, register, logout }), [user, loading, refresh, login, register, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
